/**
 * Acquisitions Wire Queue — this LOS's slice of the SHARED central closing queue.
 *
 * Postclose purchases ride the same `capital_markets.closing` collection as
 * preclose closings, tagged `queueType: 'acquisition'` so the wire team can work
 * them together but filter them apart. The backend already scopes the GET to
 * `{originatingLos: PORTAL_ID, queueType: 'acquisition'}`, so everything this
 * screen receives is in scope.
 *
 *   GET  /closing_queue                     -> queue items (see shape below)
 *   POST /closing_queue/wire_release/{id}   -> records the purchase wire, sets
 *                                              the loan to Settled, marks the
 *                                              queue item funded
 *
 * Item shape (capital_markets.upsert_acquisition_closing_queue): _id, dealId,
 * loanNumber, loanName, originator, loanStatus, subStatus, tradeId,
 * sellerCorrespondentName, settlementDate, wireReleaseDate, fundedDate,
 * currentUPB, purchasePriceAmount, queueStatus, queueType, createdAt, updatedAt.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

import { AgGridReact } from "ag-grid-react";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ScreenContent from "../../components/layout/ScreenContent";
import { losAcquisitionsApi } from "../../api/losAcquisitionsApi";
import { PIPELINES, PIPELINE_KEYS } from "../../pipelines/pipelineConfig";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";

const DRILLDOWN_BASE = PIPELINES[PIPELINE_KEYS.ACQUISITIONS].drilldownBase;

const QUEUE_STATUS_STYLE = {
  open: { bgcolor: "#eff6ff", color: "#1d4ed8" },
  funded: { bgcolor: "#ecfdf5", color: "#047857" },
};

const AcquisitionsWireQueueScreen = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [releasing, setReleasing] = useState(null); // the row being wired
  const [wireAmount, setWireAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await losAcquisitionsApi.getClosingQueue();
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      enqueueSnackbar("Could not load the wire queue", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const openRelease = useCallback((row) => {
    setReleasing(row);
    // Default the wire to the purchase price; the user can correct it before
    // release (the backend persists exactly what's sent as fundingWireAmount).
    setWireAmount(
      row?.purchasePriceAmount != null ? String(row.purchasePriceAmount) : "",
    );
  }, []);

  const handleRelease = useCallback(async () => {
    if (!releasing?.dealId) return;
    setSubmitting(true);
    try {
      const amount = wireAmount === "" ? null : Number(wireAmount);
      await losAcquisitionsApi.releaseWire(releasing.dealId, {
        fundingWireAmount: amount,
      });
      enqueueSnackbar(
        `Wire released for loan ${releasing.loanNumber ?? releasing.dealId}`,
        { variant: "success" },
      );
      setReleasing(null);
      await load();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.detail || "Could not release the wire",
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  }, [releasing, wireAmount, enqueueSnackbar, load]);

  const columnDefs = useMemo(
    () => [
      { field: "loanNumber", headerName: "Loan #", width: 110 },
      { field: "loanName", headerName: "Loan Name", flex: 1, minWidth: 160 },
      {
        field: "sellerCorrespondentName",
        headerName: "Seller",
        flex: 1,
        minWidth: 150,
      },
      { field: "tradeId", headerName: "Trade", width: 130 },
      { field: "loanStatus", headerName: "Loan Status", width: 140 },
      {
        field: "queueStatus",
        headerName: "Queue",
        width: 110,
        cellRenderer: (p) => (
          <Chip
            size="small"
            label={p.value || "--"}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "capitalize",
              ...(QUEUE_STATUS_STYLE[p.value] || {}),
            }}
          />
        ),
      },
      {
        field: "currentUPB",
        headerName: "Current UPB",
        width: 130,
        valueFormatter: (p) => formatDollar(p.value, "--"),
      },
      {
        field: "purchasePriceAmount",
        headerName: "Purchase Price",
        width: 140,
        valueFormatter: (p) => formatDollar(p.value, "--"),
      },
      {
        field: "settlementDate",
        headerName: "Settlement",
        width: 140,
        valueFormatter: (p) => formatDate(p.value, "--"),
      },
      {
        field: "wireReleaseDate",
        headerName: "Wire Released",
        width: 150,
        valueFormatter: (p) => formatDate(p.value, "--"),
      },
      {
        headerName: "",
        width: 130,
        sortable: false,
        filter: false,
        cellRenderer: (p) =>
          p.data?.queueStatus === "funded" ? null : (
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // don't trigger the row's drilldown nav
                openRelease(p.data);
              }}
            >
              Release Wire
            </Button>
          ),
      },
    ],
    [openRelease],
  );

  return (
    <ScreenContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Flx jb ac sx={{ gap: 2, flexWrap: "wrap" }}>
        <Flx column sx={{ gap: 0.25 }}>
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
            Acquisitions Wire Queue
          </Txt>
          <Txt sx={{ fontSize: 12, color: "#64748b" }}>
            Purchases awaiting settlement in the central closing queue.
          </Txt>
        </Flx>
        <Flx ac sx={{ gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search"
            value={quickFilter}
            onChange={(e) => setQuickFilter(e.target.value)}
            InputProps={{ sx: { fontSize: 13 } }}
          />
          <Button variant="text" size="small" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </Flx>
      </Flx>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AgGridTableWrapper fullHeight>
          <AgGridReact
            rowData={items}
            columnDefs={columnDefs}
            quickFilterText={quickFilter}
            getRowId={(p) => String(p.data._id)}
            pagination
            paginationPageSize={50}
            onRowClicked={(p) =>
              p.data?.dealId && navigate(`${DRILLDOWN_BASE}/${p.data.dealId}`)
            }
            rowStyle={{ cursor: "pointer" }}
          />
        </AgGridTableWrapper>
      </Box>

      <Dialog
        open={!!releasing}
        onClose={() => !submitting && setReleasing(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          Release Purchase Wire
        </DialogTitle>
        <DialogContent>
          <Flx column sx={{ gap: 2, pt: 1 }}>
            <Txt sx={{ fontSize: 12.5, color: "#475569" }}>
              This records the wire on loan{" "}
              {releasing?.loanNumber ?? releasing?.dealId}, sets it to{" "}
              <strong>Settled</strong>, and marks the queue item funded.
            </Txt>
            <TextField
              size="small"
              autoFocus
              label="Funding Wire Amount"
              value={wireAmount}
              onChange={(e) => setWireAmount(e.target.value)}
              InputProps={{ sx: { fontSize: 13 } }}
              InputLabelProps={{ sx: { fontSize: 13 } }}
            />
          </Flx>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setReleasing(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRelease}
            disabled={submitting}
          >
            {submitting ? "Releasing…" : "Release Wire"}
          </Button>
        </DialogActions>
      </Dialog>
    </ScreenContent>
  );
};

export default AcquisitionsWireQueueScreen;
